import { jsPDF } from 'jspdf';
import { installCyrillicFont } from '@/lib/pdfFont';
import { HARDWARE_CATALOG } from '@/lib/specification';

// Инструменты, которые потребуются
const TOOLS = [
  'Шуруповёрт или отвёртка крестовая PH2',
  'Шестигранник 4 мм (для минификсов)',
  'Молоток (для шкантов и задней стенки)',
  'Рулетка и угольник',
  'Карандаш',
  'Уровень',
];

// Техника безопасности и общие советы
const TIPS = [
  'Распакуйте все детали и разложите их на чистом мягком покрытии, чтобы не повредить пласти.',
  'Сверяйтесь со спецификацией: у каждой детали есть размер L × W мм.',
  'Минификсы затягивайте до упора, но без перетяга — иначе резьба сорвётся.',
  'Шканты вклеивайте на ПВА, излишки клея сразу вытирайте влажной тряпкой.',
  'Проверяйте прямоугольность каркаса рулеткой по диагоналям — разность не более 3 мм.',
];

function pushHw(map, id, qty) {
  if (!id || !qty) return;
  map[id] = (map[id] || 0) + qty;
}

function hwName(id) {
  const h = HARDWARE_CATALOG[id];
  if (!h) return id;
  return `${h.nameRu} (${h.article})`;
}

const TYPE_LABELS = {
  body: 'Корпус', frame: 'Каркас',
  back_panel: 'Задняя стенка',
  shelf: 'Полка', board: 'Панель',
  hanging_rod: 'Штанга',
  drawer: 'Ящик',
  door: 'Дверь',
  top_panel: 'Верхняя панель', tabletop: 'Столешница', countertop: 'Столешница', headboard: 'Изголовье',
  leg: 'Ножка',
};
function moduleLabel(m) {
  return m.name_ru || TYPE_LABELS[m.type] || m.type;
}

// Собирает сценарий сборки из набора модулей
export function generateAssembly(modules) {
  const bodies      = modules.filter(m => m.type === 'body' || m.type === 'frame');
  const backs       = modules.filter(m => m.type === 'back_panel');
  const shelves     = modules.filter(m => m.type === 'shelf' || m.type === 'board');
  const rods        = modules.filter(m => m.type === 'hanging_rod');
  const drawers     = modules.filter(m => m.type === 'drawer');
  const doors       = modules.filter(m => m.type === 'door');
  const tops        = modules.filter(m => ['top_panel', 'tabletop', 'countertop', 'headboard'].includes(m.type));
  const legs        = modules.filter(m => m.type === 'leg');

  const steps = [];

  // Шаг 0: подготовка
  steps.push({
    title: 'Подготовка',
    text: 'Распакуйте детали, сверьтесь со спецификацией. Рассортируйте фурнитуру по пакетам. Установите рабочее пространство — достаточно ровный пол 2 × 2 м.',
    modules: [],
    hw: {},
  });

  // Шаг 1: сборка каркаса (для каждого body отдельный подшаг, но обычно он один)
  if (bodies.length > 0) {
    const hw = {};
    bodies.forEach(() => {
      pushHw(hw, 'minifix_d15', 8);
      pushHw(hw, 'minifix_rod', 8);
      pushHw(hw, 'dowel_30x8', 4);
    });
    steps.push({
      title: bodies.length > 1 ? `Сборка ${bodies.length} каркасов` : 'Сборка каркаса',
      text: 'Соедините боковые стенки с крышей и дном с помощью минификсов и шкантов. Выровняйте диагонали каркаса: разница не должна превышать 3 мм. Затяните минификсы шестигранником.',
      modules: bodies,
      hw,
    });
  }

  // Шаг 2: задняя стенка
  if (backs.length > 0) {
    const hw = {};
    backs.forEach(() => pushHw(hw, 'nail_tole_2x20', 20));
    steps.push({
      title: 'Задняя стенка',
      text: 'Положите каркас лицевой стороной вниз. Приложите заднюю стенку ХДФ, выровняйте по краям корпуса. Прибейте толевыми гвоздями с шагом 150–200 мм по периметру и по внутренним перегородкам.',
      modules: backs,
      hw,
    });
  }

  // Шаг 3: штанги
  if (rods.length > 0) {
    steps.push({
      title: rods.length > 1 ? `Штанги (${rods.length} шт)` : 'Штанга',
      text: 'Установите держатели штанги на боковые стенки. Вставьте штангу и зафиксируйте её. Проверьте уровнем горизонтальность.',
      modules: rods,
      hw: { hinge_soft: 0 },
    });
  }

  // Шаг 4: полки
  if (shelves.length > 0) {
    const hw = {};
    shelves.forEach(() => {
      pushHw(hw, 'minifix_d15', 4);
      pushHw(hw, 'dowel_30x8', 4);
    });
    steps.push({
      title: shelves.length > 1 ? `Полки (${shelves.length} шт)` : 'Полка',
      text: 'Установите полки на полкодержатели или зафиксируйте минификсами. Проверяйте горизонтальность уровнем.',
      modules: shelves,
      hw,
    });
  }

  // Шаг 5: ящики
  if (drawers.length > 0) {
    const hw = {};
    drawers.forEach(d => {
      pushHw(hw, 'slide_ball', 1);
      const handleId = (d.hardware || []).find(h => String(h).startsWith('handle_'));
      if (handleId) pushHw(hw, handleId, 1);
    });
    steps.push({
      title: drawers.length > 1 ? `Ящики (${drawers.length} шт)` : 'Ящик',
      text: 'Прикрутите направляющие к боковинам корпуса и к боковинам ящика. Вставьте ящик на место и проверьте плавность хода. Установите ручки.',
      modules: drawers,
      hw,
    });
  }

  // Шаг 6: двери и ручки
  if (doors.length > 0) {
    const hw = {};
    doors.forEach(d => {
      pushHw(hw, 'hinge_soft', 2);
      const handleId = (d.hardware || []).find(h => String(h).startsWith('handle_'));
      if (handleId) pushHw(hw, handleId, 1);
    });
    steps.push({
      title: doors.length > 1 ? `Двери (${doors.length} шт)` : 'Дверь',
      text: 'Установите чашечные петли в подготовленные отверстия ∅35. Прикрепите ответные планки к боковинам корпуса. Навесьте двери и отрегулируйте зазоры винтами на петлях (обычно 2–3 мм).',
      modules: doors,
      hw,
    });
  }

  // Столешницы и т. п.
  if (tops.length > 0) {
    steps.push({
      title: 'Столешница / верх',
      text: 'Установите верхнюю панель и закрепите её саморезами через монтажные планки каркаса.',
      modules: tops,
      hw: {},
    });
  }

  // Ножки
  if (legs.length > 0) {
    const hw = {};
    legs.forEach(() => pushHw(hw, 'leg_decor_h145', 1));
    steps.push({
      title: legs.length > 1 ? `Ножки (${legs.length} шт)` : 'Ножка',
      text: 'Переверните корпус на мягкую подложку и прикрутите ножки снизу, стараясь разместить их симметрично.',
      modules: legs,
      hw,
    });
  }

  // Финал
  steps.push({
    title: 'Финальная проверка',
    text: 'Поставьте изделие на место. Проверьте уровень, зазоры фасадов (равномерно 2–3 мм), плавность хода ящиков и дверей. При необходимости подстройте регулировочными винтами.',
    modules: [],
    hw: {},
  });

  return steps;
}

// ---------- PDF ----------

function colorForType(type) {
  switch (type) {
    case 'body': case 'frame': return [210, 190, 160];
    case 'back_panel': return [235, 225, 210];
    case 'shelf': case 'board': return [180, 140, 90];
    case 'hanging_rod': return [160, 160, 165];
    case 'drawer': return [200, 170, 130];
    case 'door': return [150, 110, 70];
    case 'top_panel': case 'tabletop': case 'countertop': case 'headboard': return [170, 130, 90];
    case 'leg': return [90, 80, 70];
    default: return [200, 200, 200];
  }
}

// Front-view схема всех модулей, текущие (stepModules) ярче, остальные серые
function drawSchematic(pdf, modules, stepModules, x0, y0, maxW, maxH) {
  // Глобальные границы: по x/width и y/height
  if (modules.length === 0) return;
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  modules.forEach(m => {
    const mx = Number(m.x || 0);
    const my = Number(m.y || 0);
    const mw = Number(m.width || 0);
    const mh = Number(m.height || 0);
    if (mx < minX) minX = mx;
    if (my < minY) minY = my;
    if (mx + mw > maxX) maxX = mx + mw;
    if (my + mh > maxY) maxY = my + mh;
  });
  const spanX = maxX - minX;
  const spanY = maxY - minY;
  if (spanX <= 0 || spanY <= 0) return;

  const scale = Math.min(maxW / spanX, maxH / spanY);
  const drawnW = spanX * scale;
  const drawnH = spanY * scale;
  const ox = x0 + (maxW - drawnW) / 2;
  const oy = y0 + (maxH - drawnH) / 2;

  // Координаты y инвертируются: 0 внизу корпуса — вверху страницы нужно рисовать максимум
  const toPdfY = (yCm) => oy + (spanY - (yCm - minY)) * scale;

  // Сначала «фоновые» модули
  const stepSet = new Set(stepModules);
  const sorted = [...modules].sort((a, b) => {
    const ap = stepSet.has(a) ? 1 : 0;
    const bp = stepSet.has(b) ? 1 : 0;
    return ap - bp; // сначала 0 (фон), потом 1 (текущий шаг)
  });

  sorted.forEach(m => {
    const active = stepSet.has(m);
    const mx = Number(m.x || 0);
    const my = Number(m.y || 0);
    const mw = Number(m.width || 0);
    const mh = Number(m.height || 0);
    const rx = ox + (mx - minX) * scale;
    const ry = toPdfY(my + mh); // верхний край детали
    const rw = mw * scale;
    const rh = mh * scale;

    const rgb = colorForType(m.type);
    if (active) {
      pdf.setFillColor(...rgb);
      pdf.setDrawColor(30);
      pdf.setLineWidth(0.4);
      pdf.rect(rx, ry, rw, rh, 'FD');
      // Метка
      pdf.setFontSize(6.5);
      pdf.setTextColor(20);
      if (rw > 20 && rh > 8) {
        pdf.text(moduleLabel(m), rx + rw / 2, ry + rh / 2 + 1, { align: 'center' });
      }
    } else {
      pdf.setFillColor(245, 245, 245);
      pdf.setDrawColor(180);
      pdf.setLineWidth(0.15);
      pdf.rect(rx, ry, rw, rh, 'FD');
    }
  });
  pdf.setTextColor(0);

  // Рамка и подпись «Вид спереди»
  pdf.setDrawColor(150);
  pdf.setLineWidth(0.1);
  pdf.rect(ox - 2, oy - 2, drawnW + 4, drawnH + 4);
  pdf.setFontSize(7); pdf.setTextColor(120);
  pdf.text('Вид спереди', ox, oy + drawnH + 6);
  pdf.setTextColor(0);
}

export async function exportAssemblyPDF(modules, { projectName = 'Проект' } = {}) {
  const steps = generateAssembly(modules);
  if (steps.length === 0) return { steps: [] };

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  await installCyrillicFont(pdf);
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 15;

  // Титул
  pdf.setFont('Roboto', 'bold'); pdf.setFontSize(20);
  pdf.text(projectName, margin, 28);
  pdf.setFontSize(14);
  pdf.text('Инструкция по сборке', margin, 38);
  pdf.setFont('Roboto', 'normal'); pdf.setFontSize(10);
  pdf.setTextColor(80);
  pdf.text('Внимательно прочитайте инструкцию перед началом сборки.', margin, 46);
  pdf.setTextColor(0);

  let y = 60;
  pdf.setFont('Roboto', 'bold'); pdf.setFontSize(12);
  pdf.text('Потребуется инструмент', margin, y); y += 6;
  pdf.setFont('Roboto', 'normal'); pdf.setFontSize(10);
  TOOLS.forEach(t => { pdf.text(`•  ${t}`, margin, y); y += 5; });

  y += 4;
  pdf.setFont('Roboto', 'bold'); pdf.setFontSize(12);
  pdf.text('Общие советы', margin, y); y += 6;
  pdf.setFont('Roboto', 'normal'); pdf.setFontSize(10);
  TIPS.forEach(t => {
    const lines = pdf.splitTextToSize(`•  ${t}`, pageW - 2 * margin);
    pdf.text(lines, margin, y); y += 5 * lines.length;
  });

  y += 4;
  pdf.setFont('Roboto', 'bold'); pdf.setFontSize(12);
  pdf.text('Последовательность шагов', margin, y); y += 6;
  pdf.setFont('Roboto', 'normal'); pdf.setFontSize(10);
  steps.forEach((s, i) => {
    pdf.text(`${i + 1}.  ${s.title}`, margin, y); y += 5;
    if (y > pageH - 20) { pdf.addPage(); y = margin; }
  });

  // Страница на каждый шаг
  steps.forEach((step, idx) => {
    pdf.addPage();
    // Шапка шага
    pdf.setFont('Roboto', 'bold'); pdf.setFontSize(9); pdf.setTextColor(130);
    pdf.text(`${projectName}  ·  Инструкция по сборке  ·  Шаг ${idx + 1} из ${steps.length}`, margin, margin);
    pdf.setTextColor(0);
    pdf.setFontSize(18);
    pdf.text(`${idx + 1}. ${step.title}`, margin, margin + 12);

    pdf.setFont('Roboto', 'normal'); pdf.setFontSize(11);
    const descLines = pdf.splitTextToSize(step.text, pageW - 2 * margin);
    pdf.text(descLines, margin, margin + 22);

    // Левый блок: детали и крепёж
    let textY = margin + 22 + descLines.length * 5 + 6;
    pdf.setFont('Roboto', 'bold'); pdf.setFontSize(11);
    pdf.text('Детали шага', margin, textY); textY += 6;
    pdf.setFont('Roboto', 'normal'); pdf.setFontSize(10);
    if (step.modules.length === 0) {
      pdf.setTextColor(120);
      pdf.text('—', margin, textY); textY += 5;
      pdf.setTextColor(0);
    } else {
      step.modules.forEach(m => {
        pdf.text(
          `•  ${moduleLabel(m)}  ${Math.round((m.width || 0) * 10)}×${Math.round((m.height || 0) * 10)}×${Math.round((m.depth || 0) * 10)} мм`,
          margin, textY,
        );
        textY += 5;
        if (textY > pageH - 30) { pdf.addPage(); textY = margin; }
      });
    }

    textY += 4;
    pdf.setFont('Roboto', 'bold'); pdf.setFontSize(11);
    pdf.text('Крепёж', margin, textY); textY += 6;
    pdf.setFont('Roboto', 'normal'); pdf.setFontSize(10);
    const hwEntries = Object.entries(step.hw).filter(([, q]) => q > 0);
    if (hwEntries.length === 0) {
      pdf.setTextColor(120);
      pdf.text('—', margin, textY); textY += 5;
      pdf.setTextColor(0);
    } else {
      hwEntries.forEach(([id, qty]) => {
        pdf.text(`•  ${hwName(id)}  —  ${qty} шт`, margin, textY);
        textY += 5;
      });
    }

    // Правый блок: схема
    const schemX = pageW / 2 + 5;
    const schemY = margin + 35;
    const schemW = pageW - margin - schemX;
    const schemH = pageH - schemY - margin - 10;
    drawSchematic(pdf, modules, step.modules, schemX, schemY, schemW, schemH);
  });

  const safeName = (projectName || 'assembly').replace(/[^\wа-яА-Я0-9-_ ]/gi, '').slice(0, 60);
  pdf.save(`${safeName || 'assembly'}_сборка.pdf`);
  return { steps };
}
